import { useReducer, useEffect, useState } from 'react'
import api, { Order, IPageInfo, ITicket, TicketStatus } from '@api'
import { TicketKeyValueFilter, TicketListVariables } from '@api/ticket/types'
import { IGridTableCell, Toaster } from '@ui'
import { useSelection } from '@hooks'
import { ISelection } from '@hooks/useSelection'
import { uniqueId, omit } from 'lodash'
import { ApolloError } from 'apollo-boost'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  NEXT_PAGE,
  SORT,
  CLEAR_FILTERS,
  FILTER,
  QUERY,
  TICKET_STATUS,
  ERROR,
  UPDATE,
}

enum GridTableStatusEnum {
  IDLE = 'idle',
  LOADING = 'loading',
  PAGING = 'paging',
  SORTING = 'sorting',
  FILTERING = 'filtering',
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  count: number
  pageInfo: IPageInfo
  tickets: IGridTableCell<ITicket>[]
}

interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface SortAction {
  type: Actions.SORT
  column: string
}

interface FilterAction {
  type: Actions.FILTER
  keyValueFilters: TicketKeyValueFilter
}

interface ErrorAction {
  type: Actions.ERROR
  reqId: string
}

interface QueryAction {
  type: Actions.QUERY
  query: string
}

interface TicketStatusAction {
  type: Actions.TICKET_STATUS
  ticketStatus: TicketStatus | null
}

interface ClearFiltersAction {
  type: Actions.CLEAR_FILTERS
}

interface UpdateAction {
  type: Actions.UPDATE
  ticket: ITicket
}

type Action =
  | LoadAction
  | LoadStartActionWithoutSkeleton
  | LoadStartAction
  | NextPageAction
  | SortAction
  | FilterAction
  | ErrorAction
  | QueryAction
  | TicketStatusAction
  | ClearFiltersAction
  | UpdateAction

export interface IDHash {
  [id: string]: boolean
}

interface State {
  reqId: string
  count: number
  items: IGridTableCell<ITicket>[]
  query: string
  after: string | null
  first: number
  pageInfo: IPageInfo
  sort: { order: Order; column: string }
  ticketStatus: TicketStatus | null
  isLoading: boolean
  isPaging: boolean
  isSorting: boolean
  keyValueFilters: TicketKeyValueFilter
  status: GridTableStatusEnum
}

export interface UseTicketsHook extends State {
  sortBy: (column: string) => void
  nextPage: () => void
  filter: (keyValueFilters: TicketKeyValueFilter) => void
  clearFilters: () => void
  update: (ticket: ITicket) => void
  queryTable: (query: string) => void
  setTicketStatus: (status: TicketStatus | null) => void
  reload: () => void
  selection: ISelection
  fetchExportLink: () => Promise<string>
}

let persistedState: State = {
  reqId: uniqueId('request'),
  count: 0,
  items: [],
  query: '',
  after: null,
  first: PAGE_SIZE,
  pageInfo: { hasNextPage: false, endCursor: '' },
  sort: { order: Order.DESC, column: 'createdDate' },
  keyValueFilters: {
    BuildingId: null,
    EmployeeId: null,
    AssignedTo: null,
  },
  ticketStatus: null,
  isLoading: true,
  isPaging: false,
  isSorting: false,
  status: GridTableStatusEnum.LOADING,
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        isLoading: true,
        reqId: action.reqId,
        status: GridTableStatusEnum.LOADING,
      }

    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        isLoading: false,
        reqId: action.reqId,
        status: GridTableStatusEnum.IDLE,
      }

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            count: action.count,
            items:
              state.status === GridTableStatusEnum.PAGING
                ? [...state.items, ...action.tickets]
                : action.tickets,
            pageInfo: action.pageInfo,
            isLoading: false,
            isSorting: false,
            isPaging: false,
            status: GridTableStatusEnum.IDLE,
          }
        : state

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        isPaging: true,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
        status: GridTableStatusEnum.PAGING,
      }

    case Actions.FILTER:
      if (action.keyValueFilters.Building) {
        return {
          ...state,
          keyValueFilters: {
            ...omit(state.keyValueFilters, 'Floor'),
            ...action.keyValueFilters,
          },
        }
      } else if (action.keyValueFilters.Floor) {
        return {
          ...state,
          keyValueFilters: {
            ...omit(state.keyValueFilters, 'Building'),
            ...action.keyValueFilters,
          },
        }
      } else {
        return {
          ...state,
          keyValueFilters: {
            ...state.keyValueFilters,
            ...action.keyValueFilters,
          },
        }
      }

    case Actions.QUERY: {
      return {
        ...state,
        query: action.query,
      }
    }

    case Actions.TICKET_STATUS: {
      return {
        ...state,
        ticketStatus: action.ticketStatus,
      }
    }

    case Actions.CLEAR_FILTERS:
      return {
        ...state,
        keyValueFilters: {
          BuildingId: null,
          EmployeeId: null,
          AssignedTo: null,
        },
        query: '',
        ticketStatus: null,
      }

    case Actions.SORT:
      return {
        ...state,
        sort: {
          order:
            action.column === state.sort.column
              ? state.sort.order === Order.DESC
                ? Order.ASC
                : Order.DESC
              : Order.DESC,
          column: action.column,
        },
        isSorting: true,
        status: GridTableStatusEnum.SORTING,
      }

    case Actions.UPDATE: {
      const ticket: IGridTableCell<ITicket> | undefined = state.items.find(
        ({ data: { id } }) => action.ticket.id === id
      )

      const items = ticket
        ? state.items.map((t) =>
            t.data.id === action.ticket.id
              ? ({ data: action.ticket } as IGridTableCell<ITicket>)
              : t
          )
        : [{ data: action.ticket }, ...state.items]

      return {
        ...state,
        items,
      }
    }

    case Actions.ERROR:
      return action.reqId === state.reqId
        ? {
            ...state,
            isLoading: true,
            isSorting: false,
            isPaging: false,
            status: GridTableStatusEnum.IDLE,
          }
        : state

    default:
      return state
  }
}
export interface ITicketVariables {
  first?: number
  orderBy: string
  after?: string | null
  where?: any
}
interface Sort {
  column: string
  order: Order
}
interface GetVariablesFunc {
  sort: Sort
  after?: string | null
  first?: number
  keyValueFilters: TicketKeyValueFilter
  ticketStatus: TicketStatus | null
}

export function getVariables({
  sort,
  after,
  first,
  keyValueFilters,
  ticketStatus,
}: GetVariablesFunc): ITicketVariables {
  const filterList = []

  if (keyValueFilters.Building) {
    filterList.push({
      buildingId: keyValueFilters.Building?.id,
    })
  }
  if (keyValueFilters.Employee) {
    filterList.push({
      employeeId: keyValueFilters.Employee?.id,
    })
  }
  if (keyValueFilters.AssignedTo) {
    filterList.push({
      assignedToId: keyValueFilters.AssignedTo?.id,
    })
  }
  if (keyValueFilters.Floor) {
    filterList.push({
      floorId: keyValueFilters.Floor?.id,
    })
  }

  if (ticketStatus !== null) {
    filterList.push({
      status_in: ticketStatus,
    })
  }

  const where = {
    AND: filterList,
  }

  return {
    after,
    orderBy: sort.column + '_' + sort.order,
    first,
    ...(where.AND.length
      ? {
          where,
        }
      : {}),
  }
}
export default function useTickets(): UseTicketsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const [shouldReload, setShouldReload] = useState(false)
  const selection = useSelection()

  const fetchTickets =
    ({
      showSkeleton,
      loadAll,
      shouldClearSelection = true,
    }: {
      showSkeleton: boolean
      loadAll: boolean
      shouldClearSelection?: boolean
    }) =>
    () => {
      const reqId = uniqueId('request')
      const keyValueFilters: TicketKeyValueFilter[] = []
      for (const [key, value] of Object.entries(state.keyValueFilters)) {
        if (value) {
          keyValueFilters.push({ key: `${key}Id`, value: value.id })
        }
      }

      const variables: TicketListVariables = {
        status: state.ticketStatus,
        after: loadAll ? null : state.after,
        sortBy: state.sort.column,
        order: state.sort.order,
        query: state.query,
        keyValueFilters,
        first: loadAll ? state.first : PAGE_SIZE,
      }

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      setShouldReload(false)

      api.ticket
        .list(variables)
        .then((ticketsConnection) => {
          const isAllItemsSelected = selection.hasAllItemsSelected(state.items.length)
          const itemIds: string[] = []

          const items = ticketsConnection.edges.map(({ node }) => {
            if (isAllItemsSelected) {
              itemIds.push(node.id)
            }

            return { id: node.id, data: node }
          })

          //clear selection outside filtered range
          if (shouldClearSelection) {
            selection.removeSelectionsByItems(items)
          }

          // select all items if the select all is checked
          if (isAllItemsSelected && items.length) {
            selection.addAll(itemIds)
          }

          dispatch({
            type: Actions.LOAD,
            tickets: items as IGridTableCell<ITicket>[],
            pageInfo: ticketsConnection.pageInfo,
            count: ticketsConnection.counters.tickets,
            reqId,
          })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }

  //loading data without skeleton and load all items according in state.first
  useEffect(fetchTickets({ showSkeleton: false, loadAll: true }), [
    state.sort,
    state.query,
    shouldReload,
  ])

  //loading data without skeleton and load only +25 items
  useEffect(fetchTickets({ showSkeleton: false, loadAll: false, shouldClearSelection: false }), [
    state.after,
  ])

  //loading data with skeleton and load all items according in state.first
  useEffect(fetchTickets({ showSkeleton: true, loadAll: true }), [
    state.keyValueFilters,
    state.ticketStatus,
  ])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })
  const fetchExportLink = () => {
    const { orderBy, where } = getVariables({
      after: null,
      sort: state.sort,
      first: state.first,
      keyValueFilters: state.keyValueFilters,
      ticketStatus: state.ticketStatus,
    })
    return api.ticket.csv({ orderBy, where })
  }
  return {
    ...state,
    sortBy: (column) => dispatch({ type: Actions.SORT, column }),
    nextPage: () => dispatch({ type: Actions.NEXT_PAGE }),
    filter: (keyValueFilters: TicketKeyValueFilter) =>
      dispatch({ type: Actions.FILTER, keyValueFilters }),
    clearFilters: () => dispatch({ type: Actions.CLEAR_FILTERS }),
    update: (ticket: ITicket) => {
      const t = state.items.find(({ data: { id } }) => id === ticket.id)
      if (t) {
        dispatch({ type: Actions.UPDATE, ticket })
      }
    },
    queryTable: (query: string) => {
      dispatch({ type: Actions.QUERY, query })
    },
    setTicketStatus: (ticketStatus: TicketStatus | null) => {
      dispatch({ type: Actions.TICKET_STATUS, ticketStatus })
    },
    reload: () => setShouldReload(true),
    selection: selection,
    fetchExportLink,
  }
}
